import { useRef, useState, useEffect } from "react";
import { getBatchPresignedUrls, uploadToS3, completeUploads } from "@/api/uploads";

type PhotoCategory = "bride" | "groom";

interface UploadStatus {
  file: File;
  previewUrl: string;
  uploadIdx: number | null;
  fileKey: string | null;
  status: "pending" | "uploading" | "uploaded" | "error";
}

interface CategoryUploadData {
  uploadIdxList: number[];
  fileKeys: string[];
}

interface PhotoUploadProps {
  bridePhotos: File[];
  groomPhotos: File[];
  setBridePhotos: (photos: File[]) => void;
  setGroomPhotos: (photos: File[]) => void;
  onUploadsComplete: (data: {
    bride: CategoryUploadData;
    groom: CategoryUploadData;
  }) => void;
}

const MAX_PHOTOS = 2;

// ─── 카테고리별 업로드 섹션 ───
function CategoryUploadSection({
  category,
  label,
  icon,
  photos,
  setPhotos,
  statuses,
  setStatuses,
  isUploading,
  setIsUploading,
}: {
  category: PhotoCategory;
  label: string;
  icon: string;
  photos: File[];
  setPhotos: (photos: File[]) => void;
  statuses: UploadStatus[];
  setStatuses: (s: UploadStatus[]) => void;
  isUploading: boolean;
  setIsUploading: (v: boolean) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFiles(Array.from(e.target.files));
  };

  const handleFiles = async (files: File[]) => {
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    const remaining = MAX_PHOTOS - photos.length;
    const newPhotos = imageFiles.slice(0, remaining);
    if (newPhotos.length === 0) return;

    const allPhotos = [...photos, ...newPhotos];
    setPhotos(allPhotos);

    const newStatuses: UploadStatus[] = newPhotos.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      uploadIdx: null,
      fileKey: null,
      status: "pending" as const,
    }));

    const allStatuses = [...statuses, ...newStatuses];
    setStatuses(allStatuses);

    setIsUploading(true);
    try {
      const presignedData = await getBatchPresignedUrls(
        newPhotos.map((f) => ({
          fileName: f.name,
          mimeType: f.type,
          fileSize: f.size,
          photoCategory: category,
        })),
      );

      const updatedStatuses = [...allStatuses];
      const startIdx = statuses.length;

      for (let i = 0; i < newPhotos.length; i++) {
        const upload = presignedData.uploads[i];
        const statusIdx = startIdx + i;

        updatedStatuses[statusIdx] = {
          ...updatedStatuses[statusIdx],
          uploadIdx: upload.uploadIdx,
          fileKey: upload.fileKey,
          status: "uploading",
        };
        setStatuses([...updatedStatuses]);

        try {
          await uploadToS3(upload.presignedUrl, newPhotos[i]);
          updatedStatuses[statusIdx] = {
            ...updatedStatuses[statusIdx],
            status: "uploaded",
          };
        } catch {
          updatedStatuses[statusIdx] = {
            ...updatedStatuses[statusIdx],
            status: "error",
          };
        }
        setStatuses([...updatedStatuses]);
      }

      const successUploads = updatedStatuses.filter(
        (s) => s.status === "uploaded" && s.uploadIdx !== null,
      );
      if (successUploads.length > 0) {
        await completeUploads(successUploads.map((s) => s.uploadIdx!));
      }

    } catch (err) {
      console.error(`${label} 업로드 실패:`, err);
      const failedStatuses = allStatuses.map((s, idx) =>
        idx >= statuses.length && s.status === "pending"
          ? { ...s, status: "error" as const }
          : s,
      );
      setStatuses(failedStatuses);
    } finally {
      setIsUploading(false);
    }
  };

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
    setStatuses(statuses.filter((_, i) => i !== index));
  };

  return (
    <div className="border border-gray-200 rounded-2xl p-6 bg-white">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#FDF5EA]">
          <i className={`${icon} text-xl text-[#C9A96E]`}></i>
        </div>
        <div>
          <h3 className="text-base font-medium text-[#2D2D2D]">{label} 사진</h3>
          <p className="text-xs text-gray-400">
            정면·가슴 위 1장 + 살짝 측면·허리 위 1장
          </p>
        </div>
      </div>

      {/* Upload Area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
          dragActive
            ? "border-[#C9A96E] bg-[#FDF5EA]"
            : "border-gray-200 bg-gray-50 hover:border-[#C9A96E] hover:bg-[#FDFAF6]"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleChange}
          className="hidden"
        />
        <p className="text-gray-500 text-sm mb-3">
          사진을 드래그하거나 클릭하여 업로드
        </p>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading || photos.length >= MAX_PHOTOS}
          className="px-6 py-2 bg-[#C9A96E] text-white rounded-full font-medium text-sm hover:bg-[#b8935a] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploading ? (
            <span className="flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              업로드 중...
            </span>
          ) : (
            "파일 선택"
          )}
        </button>
      </div>

      {/* Photo Preview */}
      {photos.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-[#2D2D2D] mb-3">
            {photos.length}/{MAX_PHOTOS}장
          </p>
          <div className="grid grid-cols-2 gap-3">
            {photos.map((photo, index) => {
              const status = statuses[index];
              return (
                <div key={index} className="relative group">
                  <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                    <img
                      src={status?.previewUrl || URL.createObjectURL(photo)}
                      alt={`${label} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {status?.status === "uploading" && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin text-xl text-white"></i>
                      </div>
                    )}
                    {status?.status === "error" && (
                      <div className="absolute inset-0 bg-red-500/40 flex items-center justify-center">
                        <i className="ri-error-warning-line text-xl text-white"></i>
                      </div>
                    )}
                    {status?.status === "uploaded" && (
                      <div className="absolute bottom-1.5 left-1.5">
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <i className="ri-check-line text-xs text-white"></i>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => removePhoto(index)}
                    className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer hover:bg-red-600"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── 메인 컴포넌트 ───
export default function PhotoUpload({
  bridePhotos,
  groomPhotos,
  setBridePhotos,
  setGroomPhotos,
  onUploadsComplete,
}: PhotoUploadProps) {
  const [brideStatuses, setBrideStatuses] = useState<UploadStatus[]>([]);
  const [groomStatuses, setGroomStatuses] = useState<UploadStatus[]>([]);
  const [brideUploading, setBrideUploading] = useState(false);
  const [groomUploading, setGroomUploading] = useState(false);

  // statuses가 변경될 때마다 부모에게 업로드 결과 전달
  const onUploadsCompleteRef = useRef(onUploadsComplete);
  onUploadsCompleteRef.current = onUploadsComplete;

  useEffect(() => {
    const brideSuccess = brideStatuses.filter(
      (s) => s.status === "uploaded" && s.uploadIdx !== null,
    );
    const groomSuccess = groomStatuses.filter(
      (s) => s.status === "uploaded" && s.uploadIdx !== null,
    );
    onUploadsCompleteRef.current({
      bride: {
        uploadIdxList: brideSuccess.map((s) => s.uploadIdx!),
        fileKeys: brideSuccess.map((s) => s.fileKey!),
      },
      groom: {
        uploadIdxList: groomSuccess.map((s) => s.uploadIdx!),
        fileKeys: groomSuccess.map((s) => s.fileKey!),
      },
    });
  }, [brideStatuses, groomStatuses]);

  return (
    <div>
      <div className="text-center mb-10">
        <h2 className="font-serif text-3xl font-light text-[#2D2D2D] mb-4">
          사진을 업로드해주세요
        </h2>
        <p className="text-gray-500 text-sm font-light leading-relaxed">
          신부와 신랑의 사진을 각각 2장씩 업로드해주세요.
          <br />
          정면 · 가슴 위 사진 1장 + 몸을 살짝 돌린 · 허리 위 사진 1장을
          권장합니다.
        </p>
      </div>

      <div className="space-y-6">
        <CategoryUploadSection
          category="bride"
          label="신부"
          icon="ri-women-line"
          photos={bridePhotos}
          setPhotos={setBridePhotos}
          statuses={brideStatuses}
          setStatuses={setBrideStatuses}
          isUploading={brideUploading}
          setIsUploading={setBrideUploading}
        />

        <CategoryUploadSection
          category="groom"
          label="신랑"
          icon="ri-men-line"
          photos={groomPhotos}
          setPhotos={setGroomPhotos}
          statuses={groomStatuses}
          setStatuses={setGroomStatuses}
          isUploading={groomUploading}
          setIsUploading={setGroomUploading}
        />
      </div>

      {/* Photo Examples */}
      <div className="mt-10 bg-white border border-gray-200 rounded-2xl p-6">
        <p className="text-sm font-medium text-[#2D2D2D] mb-5 text-center">
          이런 사진을 올려주세요!
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Bad Example */}
          <div>
            <div className="relative rounded-xl overflow-hidden border-2 border-red-300 bg-red-50">
              <img
                src="https://spring-ai-studio.s3.ap-northeast-2.amazonaws.com/spring-ai-studio/uploads/3/Gemini_Generated_Image_zdriwozdriwozdri.png"
                alt="안좋은 예시 사진"
                className="w-full aspect-[4/5] object-cover opacity-80"
              />
              <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-500 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                <i className="ri-close-circle-line text-sm"></i>
                안좋은 예
              </div>
            </div>
            <ul className="mt-3 text-xs text-red-500 space-y-1 font-light px-1">
              <li className="flex items-start gap-1.5">
                <i className="ri-close-line mt-0.5 shrink-0"></i>
                배경이 많이 나오는 사진
              </li>
              <li className="flex items-start gap-1.5">
                <i className="ri-close-line mt-0.5 shrink-0"></i>
                어둡거나 흐린 사진
              </li>
            </ul>
          </div>

          {/* Good Example */}
          <div>
            <div className="relative rounded-xl overflow-hidden border-2 border-green-300 bg-green-50">
              <img
                src="https://spring-ai-studio.s3.ap-northeast-2.amazonaws.com/spring-ai-studio/uploads/3/Gemini_Generated_Image_z67od6z67od6z67o.png"
                alt="좋은 예시 사진"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-green-500 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                <i className="ri-checkbox-circle-line text-sm"></i>
                좋은 예
              </div>
            </div>
            <ul className="mt-3 text-xs text-green-600 space-y-1 font-light px-1">
              <li className="flex items-start gap-1.5">
                <i className="ri-check-line mt-0.5 shrink-0"></i>
                얼굴·머리·목·양쪽 어깨가 잘 보이는 사진
              </li>
              <li className="flex items-start gap-1.5">
                <i className="ri-check-line mt-0.5 shrink-0"></i>
                밝고 선명하며 정면은 가슴 위, 살짝 측면은 허리 위까지 나온 사진
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="mt-6 bg-[#FDF5EA] rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#C9A96E] shrink-0 mt-0.5">
            <i className="ri-lightbulb-line text-sm text-white"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-[#2D2D2D] mb-2">
              더 좋은 결과를 위한 팁
            </p>
            <ul className="text-xs text-gray-600 space-y-1 font-light">
              <li>- 정면 · 가슴 위 사진은 얼굴부터 가슴까지 나오게 촬영해주세요</li>
              <li>
                - 몸을 30~45도 돌리고, 얼굴은 카메라를 바라본 허리 위 사진을
                준비해주세요
              </li>
              <li>- 가까이에서 찍은 광각 셀카와 과도한 보정 필터는 피해주세요</li>
              <li>
                - 밝고 선명하며 얼굴·머리·목·양쪽 어깨가 잘리지 않은 사진을
                선택해주세요
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
